import { Injectable, inject, Injector, runInInjectionContext } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  collectionData,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  serverTimestamp
} from '@angular/fire/firestore';
import { firstValueFrom, Observable } from 'rxjs';
import { DepartmentEntry, SchemeCard } from './department-schemes.service';
import { HM_DEPARTMENT_ORDER, resolveHmDepartmentName } from '../data/hm-department-master';

export interface FirestoreDeptEntry extends DepartmentEntry {
  /** Firestore document ID */
  id: string;
}

@Injectable({ providedIn: 'root' })
export class FirestoreDepartmentService {
  private firestore = inject(Firestore);
  private injector  = inject(Injector);
  private col = collection(this.firestore, 'departments');

  // Pre-initialized in injection context — avoids AngularFire 20 context errors
  private depts$ = collectionData(this.col, { idField: 'id' }) as Observable<FirestoreDeptEntry[]>;

  private run<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  async loadDepartmentsOnce(): Promise<FirestoreDeptEntry[]> {
    const depts = await firstValueFrom(this.depts$);
    return depts ?? [];
  }

  async updateDeptSchemes(id: string, schemes: SchemeCard[]): Promise<void> {
    await this.run(() => updateDoc(doc(this.col, id), {
      schemes: this.sanitize(schemes),
      updatedAt: serverTimestamp()
    }));
  }

  async seedDepartments(departments: DepartmentEntry[]): Promise<void> {
    await Promise.all(
      departments.map(dept =>
        this.run(() => setDoc(
          doc(this.col, this.toId(dept.name)),
          this.sanitize({
            name: dept.name,
            shortName: dept.shortName,
            icon: dept.icon,
            color: dept.color,
            schemes: dept.schemes,
            isActive: true,
            updatedAt: serverTimestamp(),
          })
        ))
      )
    );
  }

  /** Keep only HM Decision master departments in Firestore; upsert canonical list. */
  async syncHmDepartmentMaster(departments: DepartmentEntry[]): Promise<{ removed: string[]; upserted: string[] }> {
    const allowed = new Set(HM_DEPARTMENT_ORDER as readonly string[]);
    const snapshot = await this.run(() => getDocs(this.col));
    const removed: string[] = [];

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data() as { name?: string };
      const name = String(data.name || '').trim();
      const canonical = resolveHmDepartmentName(name);
      if (!allowed.has(canonical) && !allowed.has(name)) {
        await this.run(() => deleteDoc(docSnap.ref));
        removed.push(name || docSnap.id);
      }
    }

    const upserted: string[] = [];
    for (const dept of departments) {
      if (!allowed.has(dept.name)) continue;
      await this.run(() => setDoc(
        doc(this.col, this.toId(dept.name)),
        this.sanitize({
          name: dept.name,
          shortName: dept.shortName,
          icon: dept.icon,
          color: dept.color,
          schemes: dept.schemes ?? [],
          isActive: true,
          updatedAt: serverTimestamp(),
        }),
        { merge: true }
      ));
      upserted.push(dept.name);
    }

    return { removed, upserted };
  }

  /** Convert a department name to a Firestore-safe document ID */
  toId(name: string): string {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  /** Remove undefined values — Firestore rejects them */
  private sanitize<T>(data: T): T {
    return JSON.parse(JSON.stringify(data));
  }
}
